// "The backend will not serve you as any organization", as heard by the request
// layers and acted on by AuthLayout.
//
// A token that carries no usable membership is refused with a 403 on every
// request, and every one of those is caught and reported as that endpoint
// failing - so the app renders, but as a shell of zeros, empty tables and a map
// that never loads. Nothing about the page tells the user the real problem, or
// that there is nothing on it they can fix. AuthLayout checks the token before
// the first request, but the backend can still refuse one that looks fine: a
// membership revoked since it was minted, an organization deactivated. This is
// how the request layers say so.
//
// Kept outside React and the store on purpose: axiosBaseQuery and
// WebServerProvider are not components, and the signal has to reach AuthLayout,
// which sits above the part of the tree that has the store's attention.

// The bodies scout-web-backend's createErrorResponse sends for an organization
// the caller may not act as (src/utils/errors.ts ERROR_MESSAGES). The body is the
// only place the reason is carried, so the two must not drift.
//
// NOT_A_SUPERUSER ("Superuser access required") is deliberately absent: it
// refuses one route, not the user, and the rest of the app still works.
const ORG_REFUSAL_BODIES = new Set([
  "No organization membership",
  "Not a member of the requested organization",
  "Organization has no access to this product",
]);

export const isOrgRefusal = (status: unknown, body: unknown): boolean =>
  status === 403 &&
  typeof body === "string" &&
  ORG_REFUSAL_BODIES.has(body.trim());

let refused = false;
const listeners = new Set<() => void>();

/**
 * Record that the backend refused this user's organization. Latches for the
 * page's lifetime: recovering means a fresh token, and AuthLayout reloads once it
 * has one, which is what clears it.
 */
export const reportOrgRefusal = (): void => {
  if (refused) return;
  refused = true;
  listeners.forEach((listener) => listener());
};

export const isOrgRefused = (): boolean => refused;

/** For useSyncExternalStore. */
export const subscribeOrgRefusal = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Tests only: the latch is module state and would otherwise leak between cases. */
export const resetOrgRefusal = (): void => {
  refused = false;
};
